import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { MenuItem } from "@mui/material";
import { JSONSchema7 } from "json-schema";
import { AppSelect } from "../../../components/AppSelect/AppSelect.component";
import { ProductSelector } from "../components/ProductSelector/ProductSelector.component";
import { QuoteExperienceState } from "../QuoteExperienceQuickSetup.page";
import { QuoteSchema } from "../../../api/quote-experience/QuoteExperienceProcessor";
import { QESchemaPanel } from "../panels/QuoteExperienceSchemaPanel/QuoteExperienceSchema.panel";
import { BrandImagesManager } from "../components/BrandImages/BrandImagesManager.component";
import { QEFormEntryMode } from "../components/QuoteExperiencePanel/QuoteExperiencePanel.component";

const PROPS_TO_IGNORE_ON_READ: string[] = ['SUPPORTED_INSURANCE_LINES'];
const CUSTOM_HANDLED_PROPERTIES: string[] = ['EXPERIENCE_TYPE', 'ORDERED_INSURANCE_LINES', 'BRAND_IMAGES'];
const ADDITIONAL_PROPERTIES_BY_EXPERIENCE_TYPE: Record<string, string[]> = {
    'cobrand': ['BRAND_IMAGES'],
    'private-label': ['BRAND_IMAGES']
}
// FormHell registers schemas by $id and requires an absolute IRI
const DEFAULT_CONFIG_SCHEMA_ID = 'urn:viu:quote-experience:config';

export const QuoteExperienceConfigForm: FC<{
    initial: Partial<QuoteExperienceState>;
    current: Partial<QuoteExperienceState>;
    schemas: Record<string, QuoteSchema>;
    bundleGuid: string | undefined;
    setIsLoading: (key: string, value: boolean) => void;
    updateData: (pointer: string, value: any, setInitial?: boolean) => void;
    formMode?: QEFormEntryMode;
    onFormModeToggle?: (formMode: QEFormEntryMode) => void;
}> = ({
    initial,
    current,
    schemas,
    bundleGuid,
    setIsLoading,
    updateData,
    formMode = 'form',
    onFormModeToggle,
}) => {
        const [experienceType, setExperienceType] = useState<string>('standard');
        const configSchema = useMemo(() => schemas['QUOTE_EXPERIENCE_CONFIG'], [schemas]);
        const configData = useMemo(() => (current.formDataByType!)['QUOTE_EXPERIENCE_CONFIG'], [current]);

        useEffect(() => {
            setExperienceType(configData?.EXPERIENCE_TYPE || 'standard');
        }, [configData]);

        const handleExperienceTypeChange = useCallback((expType: string) => {
            updateData('/formDataByType/QUOTE_EXPERIENCE_CONFIG', (current: any) => ({
                ...(current || {}),
                EXPERIENCE_TYPE: expType
            }));
            setExperienceType(expType);
        }, [updateData]);

        const getSpecialHandleProp = useCallback((key: string) => {
            switch (key) {
                case 'EXPERIENCE_TYPE':
                    const experienceTypeSchema = (configSchema.schema.properties || {})['EXPERIENCE_TYPE'] as JSONSchema7;
                    if (!experienceTypeSchema) {
                        return null;
                    }
                    return (
                        <AppSelect
                            key={key}
                            label="Experience Type"
                            required={true}
                            value={experienceType}
                            onChange={(event) => handleExperienceTypeChange(String(event.target.value))}
                        >
                            {((experienceTypeSchema.enum || []) as string[]).map(value => (
                                <MenuItem key={value} value={value}>{value}</MenuItem>
                            ))}
                        </AppSelect>
                    );
                case 'ORDERED_INSURANCE_LINES':
                    return (
                        <ProductSelector
                            key={key}
                            configSchema={configSchema}
                            configData={configData}
                            updateData={updateData}
                        />
                    );
                case 'BRAND_IMAGES':
                    return (
                        <BrandImagesManager
                            key={key}
                            initial={initial}
                            current={current}
                            bundleGuid={bundleGuid}
                            setIsLoading={setIsLoading}
                            updateData={updateData}
                        />
                    );
                default:
                    return null;
            }
        }, [configSchema, experienceType, handleExperienceTypeChange, configData, updateData, bundleGuid, current, initial, setIsLoading]);

        // Memoized: FormHell re-initializes the form for every new schema object.
        const { remainingProps, specialHandleProps } = useMemo(() => {
            if (!configSchema?.schema.properties) {
                return { remainingProps: undefined, specialHandleProps: [] as string[] };
            }
            const properties = { ...configSchema.schema.properties };
            // Special handling for ICON PACK
            if (properties['ICON_PACK'] && !(properties['ICON_PACK'] as Record<string, any>)['default']) {
                properties['ICON_PACK'] = {
                    ...(properties['ICON_PACK'] as Record<string, any>),
                    "default": 'viu'
                }
            }
            const specialHandleProps: string[] = [];
            // No additionalProperties: false - the form receives the full config data, including
            // the properties rendered by the custom components above.
            const remainingProps: JSONSchema7 = {
                ...configSchema.schema,
                "$id": `${configSchema.schema.$id || DEFAULT_CONFIG_SCHEMA_ID}_quick_${experienceType}`,
                properties: {} as NonNullable<JSONSchema7['properties']>,
                required: configSchema.schema.required ? [...configSchema.schema.required] : [],
            };
            Object.entries(properties).forEach(([key, prop]) => {
                const requiredByExpType = true; // Always true in Config Form
                const reqIdx = remainingProps.required!.indexOf(key);
                const hasDefault = (prop as Record<string, any>)['default'] !== undefined;
                const isCustom = CUSTOM_HANDLED_PROPERTIES.indexOf(key) > -1;

                // Delete from remaining props if:
                // - is in PROPS_TO_IGNORE_ON_READ
                // - is NOT required by the experience type and is NOT required by the schema
                // - is NOT required by the experience type, is required by the schema, and has a default
                // - is required by the experience or schema type and has custom handling
                if ((PROPS_TO_IGNORE_ON_READ.indexOf(key) > -1)
                || (!requiredByExpType && reqIdx === -1)
                || (!requiredByExpType && reqIdx > -1 && hasDefault)
                || (requiredByExpType && isCustom)) {
                    if (reqIdx > -1) {
                        remainingProps.required!.splice(reqIdx, 1);
                    }
                    if (isCustom) {
                        specialHandleProps.push(key);
                    }
                    return;
                }
                remainingProps.properties![key] = prop;
            });
            return { remainingProps, specialHandleProps };
        }, [configSchema, experienceType]);

        if (!remainingProps) {
            return null;
        }

        const isJsonMode = formMode === 'json';
        const additionalProps: string[] = ADDITIONAL_PROPERTIES_BY_EXPERIENCE_TYPE[experienceType] || [];

        return (
            <>
                {
                    isJsonMode ? null : specialHandleProps.map(key => getSpecialHandleProp(key))
                }
                {
                    isJsonMode || Object.keys(remainingProps.properties!).length ? (
                        <QESchemaPanel
                            schema={isJsonMode ? configSchema.schema : remainingProps}
                            schemaType='QUOTE_EXPERIENCE_CONFIG'
                            current={current}
                            updateData={updateData}
                            formMode={formMode}
                            onFormModeToggle={onFormModeToggle}
                        />
                    ) : null
                }
                {
                    isJsonMode ? null : additionalProps.map(key => getSpecialHandleProp(key))
                }
            </>
        );
    }
